/**
 * Offset REMAINDER invoices (COL-483): the parts of the model the app reads.
 *
 * An offset management invoice's fee follows the rent collected until the
 * invoice is closed, by its payout being confirmed (status "paid") or by the
 * invoice being sent. Closing it while the month's rent is still coming in
 * opens a REMAINDER for the same property, owner and month
 * (20260925140100_offset_remainder_invoices.sql), which bills the fee on the
 * rent still to come. A month's offset billing is therefore a chain:
 * remainder_seq 0 (the generator's invoice), then 1, 2, … and at most one of
 * them is open at a time.
 */

/** The invoice fields these helpers read. */
export type OffsetInvoiceFields = {
  settlement?: string | null;
  property_id?: number | string | null;
  apartment_id?: number | string | null;
  status?: string | null;
  sent_at?: string | null;
  fee_basis?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
};

/**
 * Whether an offset invoice's fee is still LIVE: it follows the rent collected
 * and the database recomputes it, so it cannot be edited by hand (the DB
 * refuses the write). The TS twin of public.offset_invoice_row_is_live
 * (the COL-474 offset_invoice_live_fee migration), narrowed to invoices that are
 * still open, which are the only ones an edit could reach.
 */
export function isLiveOffsetInvoice(row: OffsetInvoiceFields): boolean {
  if (row.settlement !== "offset") return false;
  if (row.property_id == null || row.apartment_id != null) return false;
  if (row.sent_at) return false;
  if (row.status !== "pending" && row.status !== "overdue") return false;
  const basis = row.fee_basis ?? {};
  if (basis.level !== "property") return false;
  const units = basis.properties;
  if (!Array.isArray(units)) return false;
  // A hand-created invoice's units carry no revenue model; its amount is the
  // one the admin typed, so it is not live.
  if (
    units.some((u) => !(u as { revenue_model?: unknown } | null)?.revenue_model)
  ) {
    return false;
  }
  const meta = row.metadata ?? {};
  if ("replaces_invoice_id" in meta) return false;
  if ("live_fee_overridden_at" in meta) return false;
  return true;
}

/**
 * A FORECAST row (owner-metrics projectOwnerPropertyInvoices) for an offset
 * property's month (COL-487). Creating it must go through the generator,
 * scoped to that property and owner: only the generator writes a live offset
 * invoice, while a hand-typed one never follows the rent collected, never
 * opens a remainder and holds the month's slot for good. A transfer forecast
 * is still created by hand, at the amount and due date the admin confirms.
 */
export function isOffsetPropertyForecast(row: {
  __projected?: boolean;
  __unitSettlement?: string | null;
  settlement?: string | null;
  property_id?: number | string | null;
  apartment_id?: number | string | null;
}): boolean {
  return (
    !!row.__projected &&
    (row.__unitSettlement ?? row.settlement) === "offset" &&
    row.property_id != null &&
    row.apartment_id == null
  );
}

/** A remainder: the second (or later) invoice of its month's offset billing.
 *  The invoice it follows is managementRemainderOf (management-invoice-lines),
 *  shared with the documents that print it. */
export function isRemainderInvoice(row: {
  remainder_seq?: number | null;
}): boolean {
  return (row.remainder_seq ?? 0) > 0;
}

/**
 * The DB refused to reopen an invoice (unmark its payout) because a later
 * invoice of its month (a remainder) has been paid out, sent or reissued
 * (management_invoices_offset_remainder_chain raises a check_violation).
 */
export function isRemainderReopenRefusal(err: unknown): boolean {
  const e = err as { code?: unknown; message?: unknown } | null;
  return (
    e?.code === "23514" &&
    typeof e.message === "string" &&
    e.message.includes("cannot be reopened")
  );
}

/** The fields unpaidOffsetSiblings compares. */
type OffsetSiblingFields = {
  id: string;
  settlement?: string | null;
  status?: string | null;
  lessor_id?: string | null;
  property_id?: number | string | null;
  apartment_id?: number | string | null;
  period_month?: string | null;
};

/**
 * The OTHER offset invoices of the same property, owner and month that are
 * still waiting for their payout. After an early send, the sent invoice and
 * its remainder are both unpaid, and one bank transfer can cover both, so
 * confirming one should mention the other.
 */
export function unpaidOffsetSiblings<T extends OffsetSiblingFields>(
  row: OffsetSiblingFields,
  all: readonly T[]
): T[] {
  const month = String(row.period_month ?? "").slice(0, 7);
  return all.filter(
    (other) =>
      other.id !== row.id &&
      other.settlement === "offset" &&
      other.status !== "paid" &&
      other.status !== "canceled" &&
      other.apartment_id == null &&
      other.lessor_id === row.lessor_id &&
      String(other.property_id) === String(row.property_id) &&
      String(other.period_month ?? "").slice(0, 7) === month
  );
}
